import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import UserService from '../../../services/UserService';
import StatusEnum from '../../../Enum/Status';
import { getStatusTranslationKey } from '../DashboardUtilities';
import RenderOnRole from '../../../Utilities/RenderOnRole';

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const FilterBar = ({ filters, onFilterChange, onResetFilters, relatiesVerzend, relatiesOntvangt, berichtDisplayFieldDict }) => {
  const { t } = useTranslation();
  // Local state for text-based and date filters to avoid firing per keystroke/selection
  const [local, setLocal] = useState({
    functioneelId: filters.functioneelId || '',
    panelBerichtId: filters.panelBerichtId || '',
    startDate: filters.startDate || '',
    endDate: filters.endDate || '',
  });
  const debounceRef = useRef(null);
  const latestLocalRef = useRef(local);

  useEffect(() => {
    // Keep local inputs in sync when parent filters change externally
    const next = {
      functioneelId: filters.functioneelId || '',
      panelBerichtId: filters.panelBerichtId || '',
      startDate: filters.startDate || '',
      endDate: filters.endDate || '',
    };
    setLocal(next);
    latestLocalRef.current = next;
  }, [filters.functioneelId, filters.panelBerichtId, filters.startDate, filters.endDate]);

  const scheduleCommit = (nextLocal, delay = 500) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      onFilterChange({
        ...filters,
        functioneelId: nextLocal.functioneelId,
        panelBerichtId: nextLocal.panelBerichtId,
        pageNumber: 0,
      });
    }, delay);
  };

  const commitNow = () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    onFilterChange({
      ...filters,
      functioneelId: latestLocalRef.current.functioneelId,
      panelBerichtId: latestLocalRef.current.panelBerichtId,
      startDate: latestLocalRef.current.startDate,
      endDate: latestLocalRef.current.endDate,
      pageNumber: 0,
    });
  };

  const handleImmediateChange = (field, value) => {
    onFilterChange({
      ...filters,
      [field]: value,
      pageNumber: 0, // Reset to first page when filters change
    });
  };

  // to allow multipe filter changes without causing issues with http calls in extremely quick succession which can lead to the first requests response overwriting the second request response
  const handleImmediateChanges = (newVals) => { 
    var tempFilters = structuredClone(filters);
    for(const [key, value] of Object.entries(newVals))
    {
      tempFilters[key] = value;
    }
    tempFilters.pageNumber = 0;
    onFilterChange(tempFilters);
  };

  const handleSetRelatie = (relaIndex, isSender = false) => {
    var values = {};
    var relatie;
    if(isSender)
    {
      relatie = relatiesVerzend[relaIndex];
      if(!relatie) return;
      values.verzenderGLN = relatie.gln;
      values.verzenderRelaNr = relatie.rela_Nr;
    }
    else
    {
      relatie = relatiesOntvangt[relaIndex];
      if(!relatie) return;
      values.ontvangerGLN = relatie.gln;
      values.ontvangerRelaNr = relatie.rela_Nr;
    }
    handleImmediateChanges(values);
  };

  return (
    <div className="filter-bar">
      <h3>{t('customers.searchOptions')}</h3>
      <div className="filter-grid">
        <div className="filter-group">
          <label className="filter-label">{t('messageDownload.functioneelIdLabel')}</label>
          <input
            type="text"
            value={local.functioneelId}
            onChange={(e) => {
              const next = { ...latestLocalRef.current, functioneelId: e.target.value };
              latestLocalRef.current = next;
              setLocal(next);
              scheduleCommit(next);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                commitNow();
              }
            }}
            placeholder={t('messageDownload.functioneelIdPlaceholder')}
            className="filter-input"
          />
        </div>
        <RenderOnRole roles={["Administrator", "SD_Medewerker"]}>
          <div className="filter-group">
            <label className="filter-label">{t('messageDownload.panelBerichtIdLabel')}</label>
            <input
              type="number"
              value={local.panelBerichtId}
              onChange={(e) => {
                const next = { ...latestLocalRef.current, panelBerichtId: e.target.value };
                latestLocalRef.current = next;
                setLocal(next);
                scheduleCommit(next);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  commitNow();
                }
              }}
              placeholder={t('messageDownload.panelBerichtIdPlaceholder')}
              className="filter-input"
            />
          </div>
        </RenderOnRole>
        <div className="filter-group">
          <label className="filter-label">{t('subscriptions.status')}</label>
          <select
            value={filters.status || ''}
            onChange={(e) => handleImmediateChange('status', e.target.value)}
            className="filter-select"
          >
            <option value="">{t('dashboard.status')}</option>
            {StatusEnum.map((statusLabel, index) => {
              // Hide "Handmatig verwerkt" (status 5, index 4) for Klant users
              if (index === 4 && UserService.hasRole(['Klant'])) {
                return null;
              }
              const statusNumber = index + 1;
              const translationKey = getStatusTranslationKey(statusNumber);
              return (
                <option key={index} value={statusNumber.toString()}>
                  {t(`messageStatuses.${translationKey}`)}
                </option>
              );
            })}
          </select>
        </div>
        <div className="filter-group">
          <label className="filter-label">{t('subscriptions.startDate')}</label>
          <input
            type="date"
            value={local.startDate}
            onChange={(e) => {
              const next = { ...latestLocalRef.current, startDate: e.target.value };
              latestLocalRef.current = next;
              setLocal(next);
            }}
            onBlur={commitNow}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                commitNow();
              }
            }}
            className="filter-input"
          />
        </div>
        <div className="filter-group">
          <label className="filter-label">{t('subscriptions.endDate')}</label>
          <input
            type="date"
            value={local.endDate}
            onChange={(e) => {
              const next = { ...latestLocalRef.current, endDate: e.target.value };
              latestLocalRef.current = next;
              setLocal(next);
            }}
            onBlur={commitNow}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                commitNow();
              }
            }}
            className="filter-input"
          />
        </div>
        <div className="filter-group">
          <label className="filter-label">{t('interface.sender')}</label>
          <select
            value={relatiesVerzend.findIndex(ro => {
              if(filters.verzenderRelaNr === null || filters.verzenderRelaNr === '' || filters.verzenderRelaNr === undefined)
              {
                if(filters.verzenderGLN === null || filters.verzenderGLN === '' || filters.verzenderGLN === undefined) return false;
                return ro.gln === filters.verzenderGLN;
              }
              return ro.rela_Nr === filters.verzenderRelaNr;
            })} // need to provide a value so that the display value is overwritten by filter reset
            onChange={(e) => handleSetRelatie(e.target.value, true)}
            className="filter-select"
          >
            <option value={-1}>{t('messageDownload.allSenders')}</option>
            {relatiesVerzend.map((relatie, index) => (
              <option key={index} value={index}>
                {relatie.rela_Naam} {relatie.gln !== null ? '(' + relatie.gln + ')' : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label className="filter-label">{t('interface.receiver')}</label>
          <select
            value={relatiesOntvangt.findIndex(ro => {
              if(filters.ontvangerRelaNr === null || filters.ontvangerRelaNr === '' || filters.ontvangerRelaNr === undefined)
              {
                if(filters.ontvangerGLN === null || filters.ontvangerGLN === '' || filters.ontvangerGLN === undefined) return false;
                return ro.gln === filters.ontvangerGLN;
              }
              return ro.rela_Nr === filters.ontvangerRelaNr;
            })}
            onChange={(e) => handleSetRelatie(e.target.value)}
            className="filter-select"
          >
            <option value={'{"rela_Naam": "' + t('messageDownload.allReceivers') + '", "rela_Nr" : null, "gln": null}'}>{t('messageDownload.allReceivers')}</option>
            {relatiesOntvangt.map((relatie, index) => (
              <option key={index} value={index}>
                {relatie.rela_Naam} {relatie.gln !== null ? '(' + relatie.gln + ')' : ''}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="filter-actions" style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
        <button
          className="btn btn-secondary"
          onClick={onResetFilters}
          style={{ padding: '0.5rem 1rem' }}
        >
          {t('common.reset')}
        </button>
      </div>
    </div>
  );
};

export default FilterBar;